// src/pages/PaymentFailed.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, Link } from 'react-router-dom';
import { XCircle, RefreshCw, Home, CreditCard, AlertCircle } from 'lucide-react';

const FailedContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fef2f2 0%, #fef2f2 100%);
  padding: 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const FailedCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid #fee2e2;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #dc2626 0%, #ef4444 100%);
  }
`;

const FailedIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 10px 25px rgba(220, 38, 38, 0.3);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
`;

const Message = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const ErrorDetails = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const ErrorTitle = styled.h3`
  color: #dc2626;
  font-size: 1.125rem;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ErrorItem = styled.li`
  color: #7c2d12;
  font-size: 0.9375rem;
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  
  &::before {
    content: '•';
    color: #dc2626;
    font-weight: bold;
    margin-top: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
`;

const PrimaryButton = styled(ActionButton)`
  background: linear-gradient(90deg, #dc2626 0%, #ef4444 100%);
  color: white;
  
  &:hover {
    background: linear-gradient(90deg, #b91c1c 0%, #dc2626 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3);
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: white;
  color: #dc2626;
  border: 2px solid #ef4444;
  
  &:hover {
    background: #fef2f2;
    transform: translateY(-2px);
  }
`;

const TertiaryButton = styled(ActionButton)`
  background: #f8fafc;
  color: #374151;
  border: 1px solid #e5e7eb;
  
  &:hover {
    background: #f3f4f6;
    transform: translateY(-2px);
  }
`;

const TroubleshootingSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  text-align: left;
`;

const TroubleshootingTitle = styled.h4`
  color: #111827;
  font-size: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaymentFailed = () => {
  const location = useLocation();
  const [transactionId, setTransactionId] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const txnid = params.get('txnid');
    if (txnid) {
      setTransactionId(txnid);
    }
  }, [location]);

  const commonIssues = [
    'Insufficient funds in your account',
    'Card has expired or is blocked',
    'Incorrect CVV or OTP entered',
    'Daily transaction limit exceeded',
    'Bank server timeout or maintenance',
    'Card not authorized for online payments'
  ];

  return (
    <FailedContainer>
      <FailedCard>
        <FailedIcon>
          <XCircle size={40} color="white" />
        </FailedIcon>
        
        <Title>Payment Failed</Title>
        <Message>
          We couldn't process your payment. Please try again or use a different payment method.
        </Message>

        {transactionId && (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              color: '#374151',
              fontSize: '0.875rem',
              margin: 0,
              textAlign: 'center'
            }}>
              Transaction ID: <strong>{transactionId}</strong>
            </p>
          </div>
        )}

        <ErrorDetails>
          <ErrorTitle>
            <AlertCircle size={20} />
            Common Issues
          </ErrorTitle>
          <ErrorList>
            {commonIssues.map((issue, index) => (
              <ErrorItem key={index}>{issue}</ErrorItem>
            ))}
          </ErrorList>
        </ErrorDetails>

        <ButtonGroup>
          <PrimaryButton to="/upgrade-plan">
            <RefreshCw size={20} />
            Try Payment Again
          </PrimaryButton>
          
          <SecondaryButton to="/">
            <Home size={20} />
            Go to Dashboard
          </SecondaryButton>
          
          <TertiaryButton to="/settings">
            <CreditCard size={20} />
            Update Payment Methods
          </TertiaryButton>
        </ButtonGroup>

        <TroubleshootingSection>
          <TroubleshootingTitle>
            <AlertCircle size={18} />
            Need Help?
          </TroubleshootingTitle>
          <p style={{ 
            color: '#6b7280',
            fontSize: '0.875rem',
            margin: '0.5rem 0',
            lineHeight: '1.6'
          }}>
            If you continue to experience issues:
          </p>
          <ul style={{
            color: '#6b7280',
            fontSize: '0.875rem',
            paddingLeft: '1.5rem',
            margin: '0.5rem 0',
            lineHeight: '1.6'
          }}>
            <li>Try using a different card or payment method</li>
            <li>Contact your bank to ensure online payments are enabled</li>
            <li>Wait a few minutes and try again</li>
          </ul>
          <div style={{ 
            marginTop: '1rem',
            padding: '1rem',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <p style={{ 
              color: '#374151',
              fontSize: '0.875rem',
              margin: 0,
              fontWeight: 500
            }}>
              Support: <a href="mailto:support@yourdomain.com" style={{ color: '#3b82f6' }}>support@yourdomain.com</a> • Phone: +91-XXXXXXXXXX
            </p>
          </div>
        </TroubleshootingSection>

        <div style={{ 
          marginTop: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{ 
            fontSize: '0.75rem',
            color: '#9ca3af',
            margin: 0,
            textAlign: 'center'
          }}>
            Your funds are safe. No money has been deducted from your account.
          </p>
        </div>
      </FailedCard>
    </FailedContainer>
  );
};

export default PaymentFailed;